/**
 * IHS widget envelope schemas.
 *
 * Permissive runtime schemas for the IHS bundle. Each schema validates
 * only the SHAPE the matching widget actually consumes — extra fields
 * pass through (`.passthrough()`), unknown fields don't cause a parse
 * failure. The goal is to surface engine schema drift in the dev
 * console BEFORE a widget crashes inside its render path, not to
 * police every contract field.
 *
 * Why so loose: the canonical types in `./ihs.ts` describe the full
 * engine contract, but the components only read a subset. Validating
 * the entire contract here would force the test fixtures to ship every
 * field whether the test cares or not. The `safeParse` budget is also
 * low — `<1ms` per envelope.
 *
 * If a future widget starts reading a field that's currently optional
 * in the schema, tighten the schema rather than the type. Schema first,
 * widget second.
 */

import { z } from 'zod';

// ─── Shared primitives ───────────────────────────────────────────────────

const HeaderSchema = z
  .object({
    emoji: z.string().optional(),
    title: z.string().optional(),
    subtitle: z.string().optional(),
  })
  .partial()
  .passthrough()
  .optional();

// Provenance — engine ships either the new `{origin, algorithm,
// generatedAt}` shape or older `{generatedAt, source}`. We just check
// it's an object so the validator doesn't reject either.
const ProvenanceSchema = z.record(z.unknown()).optional();

// Team shape — at minimum the widgets need `name`. Other fields (id,
// side, badge, shortCode, jerseyPrimary, jerseySecondary) vary by
// engine vintage and may be absent.
const TeamShapeSchema = z
  .object({ name: z.string() })
  .passthrough();

// League shape — overview reads `{name, id}`; both required.
const LeagueShapeSchema = z
  .object({ name: z.string(), id: z.string() })
  .passthrough();

// ─── 1. Overview ─────────────────────────────────────────────────────────

const OverviewSideSchema = z
  .object({
    team: TeamShapeSchema,
    league: LeagueShapeSchema,
    rank: z.number().nullable().optional(),
    played: z.number().nullable().optional(),
    wins: z.number().nullable().optional(),
    draws: z.number().nullable().optional(),
    losses: z.number().nullable().optional(),
    points: z.number().nullable().optional(),
  })
  .passthrough();

export const IhsOverviewSchema = z
  .object({
    header: HeaderSchema,
    home: OverviewSideSchema,
    away: OverviewSideSchema,
    provenance: ProvenanceSchema,
  })
  .passthrough();

// ─── 2. Previous meetings ────────────────────────────────────────────────

export const IhsPreviousMeetingsSchema = z
  .object({
    header: HeaderSchema,
    home: TeamShapeSchema,
    away: TeamShapeSchema,
    totalMeetings: z.number(),
    homeWins: z.number(),
    draws: z.number(),
    awayWins: z.number(),
    window: z.string().optional(),
    provenance: ProvenanceSchema,
  })
  .passthrough();

// ─── 3. Victory probability ──────────────────────────────────────────────

export const IhsVictoryProbabilitySchema = z
  .object({
    header: HeaderSchema,
    home: TeamShapeSchema,
    away: TeamShapeSchema,
    homePct: z.number(),
    drawPct: z.number(),
    awayPct: z.number(),
    method: z.string().optional(),
    oddsFetchedAt: z.string().nullable().optional(),
    provenance: ProvenanceSchema,
  })
  .passthrough();

// ─── 4. Last 5 games ─────────────────────────────────────────────────────

const LastFiveGamesSideSchema = z
  .object({
    team: TeamShapeSchema,
    games: z.array(z.unknown()),
  })
  .passthrough();

export const IhsLastFiveGamesSchema = z
  .object({
    header: HeaderSchema,
    home: LastFiveGamesSideSchema,
    away: LastFiveGamesSideSchema,
    provenance: ProvenanceSchema,
  })
  .passthrough();

// ─── 5. Top scorers ──────────────────────────────────────────────────────

const TopScorersSideSchema = z
  .object({
    team: TeamShapeSchema,
    scorers: z.array(z.unknown()),
  })
  .passthrough();

export const IhsTopScorersSchema = z
  .object({
    header: HeaderSchema,
    home: TopScorersSideSchema,
    away: TopScorersSideSchema,
    provenance: ProvenanceSchema,
  })
  .passthrough();

// ─── 6. Season stats ─────────────────────────────────────────────────────

const SeasonStatsSideSchema = z
  .object({
    team: TeamShapeSchema,
  })
  .passthrough();

export const IhsSeasonStatsSchema = z
  .object({
    header: HeaderSchema,
    seasonLabel: z.string().optional(),
    competitionName: z.string().optional(),
    home: SeasonStatsSideSchema,
    away: SeasonStatsSideSchema,
    provenance: ProvenanceSchema,
  })
  .passthrough();

// ─── 7. Goals ────────────────────────────────────────────────────────────

const GoalsSideSchema = z
  .object({
    team: TeamShapeSchema,
  })
  .passthrough();

export const IhsGoalsSchema = z
  .object({
    header: HeaderSchema,
    home: GoalsSideSchema,
    away: GoalsSideSchema,
    matchesAnalyzed: z.number().optional(),
    provenance: ProvenanceSchema,
  })
  .passthrough();

// ─── 8. Over goals ───────────────────────────────────────────────────────

export const IhsOverGoalsSchema = z
  .object({
    header: HeaderSchema,
    home: TeamShapeSchema,
    away: TeamShapeSchema,
    lines: z.array(z.unknown()),
    provenance: ProvenanceSchema,
  })
  .passthrough();

// ─── 9. Shots ────────────────────────────────────────────────────────────

const ShotsSideSchema = z
  .object({
    team: TeamShapeSchema,
  })
  .passthrough();

export const IhsShotsSchema = z
  .object({
    header: HeaderSchema,
    home: ShotsSideSchema,
    away: ShotsSideSchema,
    provenance: ProvenanceSchema,
  })
  .passthrough();

// ─── 10. Avg match stats ─────────────────────────────────────────────────

const AvgMatchStatsSideSchema = z
  .object({
    team: TeamShapeSchema,
  })
  .passthrough();

export const IhsAvgMatchStatsSchema = z
  .object({
    header: HeaderSchema,
    home: AvgMatchStatsSideSchema,
    away: AvgMatchStatsSideSchema,
    provenance: ProvenanceSchema,
  })
  .passthrough();

// ─── 11. Venue + referee ─────────────────────────────────────────────────

export const IhsVenueRefereeSchema = z
  .object({
    header: HeaderSchema,
    venue: z.record(z.unknown()),
    referee: z.record(z.unknown()),
    provenance: ProvenanceSchema,
  })
  .passthrough();

// ─── 12. Popular odds ────────────────────────────────────────────────────

export const IhsPopularOddsSchema = z
  .object({
    header: HeaderSchema,
    markets: z.array(z.unknown()),
    defaultMarketId: z.string().optional(),
    homeTeamName: z.string().optional(),
    awayTeamName: z.string().optional(),
    matchEventId: z.string().optional(),
    provenance: ProvenanceSchema,
  })
  .passthrough();

// ─── Lookup table ────────────────────────────────────────────────────────

export const IHS_SCHEMAS = {
  'ihs-overview': IhsOverviewSchema,
  'ihs-previous-meetings': IhsPreviousMeetingsSchema,
  'ihs-victory-probability': IhsVictoryProbabilitySchema,
  'ihs-last-5-games': IhsLastFiveGamesSchema,
  'ihs-top-scorers': IhsTopScorersSchema,
  'ihs-season-stats': IhsSeasonStatsSchema,
  'ihs-goals': IhsGoalsSchema,
  'ihs-over-goals': IhsOverGoalsSchema,
  'ihs-shots': IhsShotsSchema,
  'ihs-avg-match-stats': IhsAvgMatchStatsSchema,
  'ihs-venue-referee': IhsVenueRefereeSchema,
  'ihs-popular-odds': IhsPopularOddsSchema,
} as const;

export type IhsTemplateId = keyof typeof IHS_SCHEMAS;
